export const GTM_ID = 'GTM-5PDZ3NSQ';

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface DataLayerEvent {
  event: string;
  [key: string]: unknown;
}

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    dataLayer?: DataLayerEvent[];
  }
}

/**
 * Pushes raw event data to the global dataLayer.
 * Client-side only.
 */
export const pushToDataLayer = (data: DataLayerEvent): void => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  }
};

/**
 * Emits a single SPA page_view event.
 */
export const pushPageView = (url: string): void => {
  let path = url;
  try {
    const parsedUrl = new URL(url, window.location.origin);
    path = parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
  } catch (error) {
    // fallback to raw URL if parsing fails
    console.warn('[Analytics] Failed to parse URL for page_view', error);
  }

  pushToDataLayer({
    event: 'page_view',
    page_location: url,
    page_path: path,
  });
};

/**
 * Normalizes integer cents into major currency units (USD dollars).
 */
export const centsToUsd = (cents: number): number => {
  return cents / 100;
};

/**
 * Types for GA4 eCommerce and other custom events.
 */
export type TGa4EventParams = {
  currency?: string;
  /** Major currency units (e.g. USD dollars). */
  value?: number;
  [key: string]: unknown;
};

/**
 * Emits a GA4 event. Automatically handles currency normalization if cents are provided.
 */
export const pushGa4Event = (
  eventName: string,
  params?: TGa4EventParams & { valueInCents?: number },
): void => {
  const { valueInCents, ...restParams } = params || {};

  const finalParams: TGa4EventParams = { ...restParams };

  if (valueInCents !== undefined) {
    finalParams.value = centsToUsd(valueInCents);
    finalParams.currency = finalParams.currency || 'USD';
  }

  pushToDataLayer({
    event: eventName,
    ...finalParams,
  });
};

/**
 * Emits a generic custom event.
 */
export const pushCustomEvent = (
  eventName: string,
  params?: Record<string, unknown>,
): void => {
  pushToDataLayer({
    event: eventName,
    ...params,
  });
};
